import { useCallback } from 'react'
import { GeoJSON } from 'geojson'

import { CreateTaskInitialData } from '@/feature/execution-board'
import { TaskLocation } from '@/feature/execution-board/components/TaskLocationPopout'

import { EtaDistanceResult, EtaLocation, EtaLocationType } from '../types/EtaLocation.types'

const buildTemporaryGeoJson = (latitude: number, longitude: number): GeoJSON => ({
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: [latitude, longitude],
  },
  properties: {
    radius: 100,
    home: { lat: latitude, lng: longitude },
  },
})

const findVehicle = (
  etaLocationFrom: EtaLocation,
  etaLocationTo: EtaLocation,
): EtaLocation | undefined => {
  if (etaLocationFrom.type === EtaLocationType.ASSET) return etaLocationFrom
  if (etaLocationTo.type === EtaLocationType.ASSET) return etaLocationTo
  return undefined
}

const findDestination = (
  etaLocationFrom: EtaLocation,
  etaLocationTo: EtaLocation,
): EtaLocation | undefined => {
  if (etaLocationTo.type === EtaLocationType.GEOFENCE) return etaLocationTo
  if (etaLocationFrom.type === EtaLocationType.GEOFENCE) return etaLocationFrom
  return undefined
}

const vehicleToTaskLocation = (vehicle: EtaLocation): TaskLocation => ({
  id: null,
  name: vehicle.address,
  type: 'temporary',
  address: vehicle.address,
  geoJson: buildTemporaryGeoJson(vehicle.latitude, vehicle.longitude),
})

const geofenceToTaskLocation = (geofence: EtaLocation): TaskLocation => ({
  id: (geofence.id as number) ?? null,
  name: geofence.title ?? geofence.address,
  type: geofence.id ? 'geofence' : 'temporary',
  address: geofence.address,
  ...(!geofence.id && {
    geoJson: buildTemporaryGeoJson(geofence.latitude, geofence.longitude),
  }),
})

const formatDatetimeLocal = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

interface UseEtaToTaskProps {
  etaLocationFrom: EtaLocation
  etaLocationTo: EtaLocation
  etaResult: EtaDistanceResult | undefined
}

export const useEtaToTask = ({
  etaLocationFrom,
  etaLocationTo,
  etaResult,
}: UseEtaToTaskProps): (() => CreateTaskInitialData) | undefined => {
  const buildTaskData = useCallback((): CreateTaskInitialData | undefined => {
    if (!etaResult?.duration) return undefined

    const vehicle = findVehicle(etaLocationFrom, etaLocationTo)
    const destination = findDestination(etaLocationFrom, etaLocationTo)

    if (!vehicle || !destination) return undefined

    const now = new Date()
    const FIVE_MINUTES_MS = 5 * 60 * 1000
    const departureTime = new Date(now.getTime() + FIVE_MINUTES_MS)
    const etaArrivalTime = new Date(departureTime.getTime() + etaResult.duration.value * 1000)

    return {
      fromLocation: vehicleToTaskLocation(vehicle),
      toLocation: geofenceToTaskLocation(destination),
      fromDatetime: formatDatetimeLocal(departureTime),
      toDatetime: formatDatetimeLocal(etaArrivalTime),
    }
  }, [etaLocationFrom, etaLocationTo, etaResult])

  if (!etaResult?.duration) return undefined

  return buildTaskData as () => CreateTaskInitialData
}
